import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, List, ListItem, ListItemText,
  MenuItem, Paper, Select, TextField, Tooltip, Typography
} from '@mui/material';
import {Add, Edit, Group, Dns} from '@mui/icons-material';

import {
  listKannelBoxes, upsertKannelBox,
  listBoxOperators, setBoxOperators,
  listBoxSmsboxes, setBoxSmsboxes,
  listOperators
} from '../../actions/manageOperators';
import Table from '../../components/Table';

const LOG_LEVELS = [
  {value: 0, label: '0 — debug'},
  {value: 1, label: '1 — info (default)'},
  {value: 2, label: '2 — warning'},
  {value: 3, label: '3 — error'},
  {value: 4, label: '4 — panic'}
];

class KannelBoxes extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      boxes: [],
      operators: [],
      // edit-box dialog
      showEdit: false,
      editBox: null,
      editForm: {boxId: '', displayName: '', smsboxMaxPending: 50000, logLevel: 1},
      isNewBox: false,
      // operators dialog
      showOps: false,
      opsBox: null,
      opsSelected: new Set(),
      opsLoading: false,
      // smsbox-clients dialog
      showSmsbox: false,
      smsboxBox: null,
      smsboxList: [],
      smsboxInput: '',
      smsboxLoading: false,
      showWait: false,
      errorMsg: null
    };
    this.boxesReq = true;
    this.operatorsReq = true;
    this.upsertReq = false;
    this.opsListReq = false;
    this.opsSetReq = false;
    this.smsboxListReq = false;
    this.smsboxSetReq = false;
  }

  componentDidMount() {
    this.props.listKannelBoxes();
    this.props.listOperators();
  }

  componentWillReceiveProps(next) {
    if (this.boxesReq && next.kannelBoxesList) {
      this.boxesReq = false;
      if (next.kannelBoxesList.success) {
        const boxes = next.kannelBoxesList.data?.boxes || [];
        this.setState({boxes});
      } else {
        this.setState({errorMsg: next.kannelBoxesList.message || 'Failed to load kannel boxes'});
      }
      this.checkLoading();
    }
    if (this.operatorsReq && next.operatorsList?.success) {
      this.operatorsReq = false;
      this.setState({operators: next.operatorsList.data?.operators || []});
      this.checkLoading();
    }
    if (this.upsertReq && next.upsertBoxRes) {
      this.upsertReq = false;
      this.setState({showWait: false});
      if (next.upsertBoxRes.success) {
        this.setState({showEdit: false, editBox: null, isNewBox: false});
        this.boxesReq = true;
        this.props.listKannelBoxes();
      } else {
        alert(next.upsertBoxRes.message || 'Failed to save box');
      }
    }
    if (this.opsListReq && next.boxOperatorsRes) {
      this.opsListReq = false;
      this.setState({opsLoading: false});
      if (next.boxOperatorsRes.success) {
        const ids = next.boxOperatorsRes.data?.operatorIds
          || next.boxOperatorsRes.data?.operator_ids
          || [];
        this.setState({opsSelected: new Set(ids)});
      } else {
        alert(next.boxOperatorsRes.message || 'Failed to load box operators');
      }
    }
    if (this.opsSetReq && next.setBoxOpsRes) {
      this.opsSetReq = false;
      this.setState({showWait: false});
      if (next.setBoxOpsRes.success) {
        this.setState({showOps: false, opsBox: null});
      } else {
        alert(next.setBoxOpsRes.message || 'Failed to update box operators');
      }
    }
    if (this.smsboxListReq && next.boxSmsboxesRes) {
      this.smsboxListReq = false;
      this.setState({smsboxLoading: false});
      if (next.boxSmsboxesRes.success) {
        const ids = next.boxSmsboxesRes.data?.smsboxIds
          || next.boxSmsboxesRes.data?.smsbox_ids
          || [];
        this.setState({smsboxList: ids});
      } else {
        alert(next.boxSmsboxesRes.message || 'Failed to load box smsbox clients');
      }
    }
    if (this.smsboxSetReq && next.setBoxSmsboxesRes) {
      this.smsboxSetReq = false;
      this.setState({showWait: false});
      if (next.setBoxSmsboxesRes.success) {
        this.setState({showSmsbox: false, smsboxBox: null});
      } else {
        alert(next.setBoxSmsboxesRes.message || 'Failed to update box smsbox clients');
      }
    }
  }

  checkLoading() {
    if (!this.boxesReq && !this.operatorsReq) this.setState({loading: false});
  }

  openEdit = (box) => {
    this.setState({
      showEdit: true,
      editBox: box,
      isNewBox: false,
      editForm: {
        boxId: box.boxId || box.box_id,
        displayName: box.displayName || box.display_name || '',
        smsboxMaxPending: box.smsboxMaxPending ?? box.smsbox_max_pending ?? 50000,
        logLevel: box.logLevel ?? box.log_level ?? 1
      }
    });
  };

  openAdd = () => {
    this.setState({
      showEdit: true,
      editBox: null,
      isNewBox: true,
      editForm: {boxId: '', displayName: '', smsboxMaxPending: 50000, logLevel: 1}
    });
  };

  saveBox = () => {
    const f = this.state.editForm;
    if (!f.boxId || !/^[a-z0-9_-]+$/i.test(f.boxId)) {
      alert('Box ID is required and must be alphanumeric (underscore/hyphen allowed)');
      return;
    }
    this.upsertReq = true;
    this.setState({showWait: true});
    this.props.upsertKannelBox(f.boxId, f);
  };

  openOps = (box) => {
    const boxId = box.boxId || box.box_id;
    this.setState({showOps: true, opsBox: box, opsLoading: true, opsSelected: new Set()});
    this.opsListReq = true;
    this.props.listBoxOperators(boxId);
  };

  toggleOp = (opId) => {
    const next = new Set(this.state.opsSelected);
    if (next.has(opId)) next.delete(opId); else next.add(opId);
    this.setState({opsSelected: next});
  };

  saveOps = () => {
    const box = this.state.opsBox;
    if (!box) return;
    const boxId = box.boxId || box.box_id;
    this.opsSetReq = true;
    this.setState({showWait: true});
    this.props.setBoxOperators(boxId, Array.from(this.state.opsSelected));
  };

  openSmsbox = (box) => {
    const boxId = box.boxId || box.box_id;
    this.setState({showSmsbox: true, smsboxBox: box, smsboxLoading: true, smsboxList: [], smsboxInput: ''});
    this.smsboxListReq = true;
    this.props.listBoxSmsboxes(boxId);
  };

  addSmsbox = () => {
    const id = (this.state.smsboxInput || '').trim();
    if (!id) return;
    if (!/^[a-z0-9_-]+$/i.test(id)) {
      alert('smsbox-id must be alphanumeric (underscore/hyphen allowed) — it matches the worker BOXC_ID');
      return;
    }
    if (this.state.smsboxList.includes(id)) {
      this.setState({smsboxInput: ''});
      return;
    }
    this.setState({smsboxList: [...this.state.smsboxList, id], smsboxInput: ''});
  };

  removeSmsbox = (id) => {
    this.setState({smsboxList: this.state.smsboxList.filter(x => x !== id)});
  };

  saveSmsboxes = () => {
    const box = this.state.smsboxBox;
    if (!box) return;
    if (this.state.smsboxList.length === 0) {
      alert('A kannel box must have at least one smsbox client (an empty set makes bearerbox panic).');
      return;
    }
    const boxId = box.boxId || box.box_id;
    this.smsboxSetReq = true;
    this.setState({showWait: true});
    this.props.setBoxSmsboxes(boxId, this.state.smsboxList);
  };

  render() {
    const {loading, boxes, operators, showEdit, editForm, isNewBox, showOps, opsBox, opsSelected, opsLoading, showSmsbox, smsboxBox, smsboxList, smsboxInput, smsboxLoading, showWait, errorMsg} = this.state;

    if (loading) {
      return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress /></Box>;
    }

    return (
      <div>
        <div style={{display: 'flex', alignItems: 'center', padding: '8px 0 16px'}}>
          <div>
            <Typography variant="h6" sx={{fontWeight: 600}}>Kannel Boxes</Typography>
            <p style={{margin: 0, fontSize: 12, color: 'rgba(0,0,0,0.6)'}}>
              Per-box smsbox capacity + log level + operator visibility. The <code>default</code> box
              is the legacy single-box behaviour; create extra boxes only when you want to partition
              operator binds across multiple bearerbox instances.
            </p>
          </div>
          <div style={{marginLeft: 'auto'}}>
            <Button variant="contained" startIcon={<Add />} onClick={this.openAdd}>Add Box</Button>
          </div>
        </div>

        {errorMsg && <Alert severity="error" sx={{mb: 2}}>{errorMsg}</Alert>}

        <Table
          column={[
            {name: 'Box ID', value: 'boxId', cell: (v, row) => (
              <Chip label={v || row.box_id} size="small" color={(v || row.box_id) === 'default' ? 'primary' : 'default'} />
            )},
            {name: 'Display Name', value: 'displayName', cell: (v, row) => v || row.display_name || '—'},
            {name: 'smsbox-max-pending', value: 'smsboxMaxPending', cell: (v, row) => v ?? row.smsbox_max_pending ?? '—'},
            {name: 'log-level', value: 'logLevel', cell: (v, row) => {
              const lv = v ?? row.log_level ?? 1;
              const lvl = LOG_LEVELS.find(x => x.value === lv);
              return lvl ? lvl.label : String(lv);
            }},
            {name: 'Updated', value: 'updatedAt', cell: (v, row) => {
              const t = v || row.updated_at;
              return t ? new Date(t).toLocaleString() : '—';
            }},
            {name: 'Actions', value: 'boxId', style: {width: 150}, cell: (v, row) => (
              <div style={{display: 'flex', gap: 2}}>
                <Tooltip title="Edit settings">
                  <IconButton size="small" onClick={(e) => {e.stopPropagation(); this.openEdit(row);}}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit operator visibility">
                  <IconButton size="small" onClick={(e) => {e.stopPropagation(); this.openOps(row);}}>
                    <Group fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit smsbox clients">
                  <IconButton size="small" onClick={(e) => {e.stopPropagation(); this.openSmsbox(row);}}>
                    <Dns fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          ]}
          serialNumber
          data={boxes}
        />

        {/* Edit / Add box dialog */}
        <Dialog open={showEdit} onClose={() => this.setState({showEdit: false})} maxWidth="sm" fullWidth>
          <DialogTitle>{isNewBox ? 'Add Kannel Box' : `Edit Box: ${editForm.boxId}`}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
              <TextField
                label="Box ID"
                size="small"
                value={editForm.boxId}
                onChange={(e) => this.setState({editForm: {...editForm, boxId: e.target.value}})}
                disabled={!isNewBox}
                helperText={isNewBox ? 'Lowercase alphanumeric, underscore/hyphen ok. Identifies the bearerbox instance.' : 'Box ID is immutable after creation.'}
              />
              <TextField
                label="Display Name"
                size="small"
                value={editForm.displayName}
                onChange={(e) => this.setState({editForm: {...editForm, displayName: e.target.value}})}
                helperText="Human-friendly label shown in the UI."
              />
              <TextField
                label="smsbox-max-pending"
                size="small"
                type="number"
                value={editForm.smsboxMaxPending}
                onChange={(e) => this.setState({editForm: {...editForm, smsboxMaxPending: e.target.value}})}
                helperText="Max in-flight submits queued between smsbox and bearerbox. Default 50000."
              />
              <Box>
                <Typography variant="caption" sx={{display: 'block', mb: 0.5}}>log-level</Typography>
                <Select
                  size="small"
                  fullWidth
                  value={editForm.logLevel}
                  onChange={(e) => this.setState({editForm: {...editForm, logLevel: parseInt(e.target.value)}})}
                >
                  {LOG_LEVELS.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                </Select>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({showEdit: false})}>Cancel</Button>
            <Button variant="contained" onClick={this.saveBox} disabled={showWait}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Operator visibility dialog */}
        <Dialog open={showOps} onClose={() => this.setState({showOps: false})} maxWidth="sm" fullWidth>
          <DialogTitle>
            Operators in box <code>{opsBox?.boxId || opsBox?.box_id}</code>
          </DialogTitle>
          <DialogContent dividers>
            {opsLoading ? (
              <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}><CircularProgress size={24} /></Box>
            ) : (
              <>
                <Typography variant="caption" sx={{display: 'block', mb: 1, color: 'rgba(0,0,0,0.6)'}}>
                  Select which SMSC operators are visible to this kannel box. The bearerbox running with
                  this <code>box_id</code> will only see these operators in its kannel.conf.
                </Typography>
                <List dense sx={{maxHeight: 360, overflow: 'auto'}}>
                  {operators.map(op => (
                    <ListItem
                      key={op.id}
                      onClick={() => this.toggleOp(op.id)}
                      sx={{cursor: 'pointer', borderRadius: 1, '&:hover': {background: 'rgba(0,0,0,0.04)'}}}
                    >
                      <Checkbox edge="start" checked={opsSelected.has(op.id)} tabIndex={-1} disableRipple />
                      <ListItemText
                        primary={op.code || op.name}
                        secondary={`${op.host || ''}:${op.port || ''}  •  ${op.protocol || 'smpp'}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({showOps: false})}>Cancel</Button>
            <Button variant="contained" onClick={this.saveOps} disabled={showWait || opsLoading}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Smsbox-clients dialog */}
        <Dialog open={showSmsbox} onClose={() => this.setState({showSmsbox: false})} maxWidth="sm" fullWidth>
          <DialogTitle>
            Smsbox clients in box <code>{smsboxBox?.boxId || smsboxBox?.box_id}</code>
          </DialogTitle>
          <DialogContent dividers>
            {smsboxLoading ? (
              <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}><CircularProgress size={24} /></Box>
            ) : (
              <>
                <Typography variant="caption" sx={{display: 'block', mb: 1, color: 'rgba(0,0,0,0.6)'}}>
                  The smsbox clients (worker fleets, by their <code>smsbox-id</code> / <code>BOXC_ID</code>)
                  that may bind to this bearerbox instance. config-sync renders one
                  <code> group = smsbox</code> per id.
                </Typography>
                <Alert severity="warning" sx={{mb: 2}}>
                  Changes take effect only after a <strong>bearerbox restart</strong> (Kannel has no
                  add-smsbox admin command). A box must keep at least one smsbox client.
                </Alert>
                <Box sx={{display: 'flex', gap: 1, mb: 2}}>
                  <TextField
                    label="Add smsbox-id"
                    size="small"
                    fullWidth
                    value={smsboxInput}
                    onChange={(e) => this.setState({smsboxInput: e.target.value})}
                    onKeyDown={(e) => {if (e.key === 'Enter') {e.preventDefault(); this.addSmsbox();}}}
                    placeholder="e.g. sms-worker"
                  />
                  <Button variant="outlined" onClick={this.addSmsbox}>Add</Button>
                </Box>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                  {smsboxList.length === 0 && (
                    <Typography variant="body2" sx={{color: 'rgba(0,0,0,0.5)'}}>No smsbox clients — add at least one.</Typography>
                  )}
                  {smsboxList.map(id => (
                    <Chip key={id} label={id} onDelete={() => this.removeSmsbox(id)} />
                  ))}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({showSmsbox: false})}>Cancel</Button>
            <Button variant="contained" onClick={this.saveSmsboxes} disabled={showWait || smsboxLoading || smsboxList.length === 0}>Save</Button>
          </DialogActions>
        </Dialog>

        {showWait && (
          <Dialog open>
            <DialogContent sx={{display: 'flex', alignItems: 'center', gap: 2}}>
              <CircularProgress size={20} />
              <Typography>Saving…</Typography>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    kannelBoxesList: state.manageOperators.listKannelBoxesResponse,
    operatorsList: state.manageOperators.listOperatorsResponse,
    upsertBoxRes: state.manageOperators.upsertKannelBoxResponse,
    boxOperatorsRes: state.manageOperators.listBoxOperatorsResponse,
    setBoxOpsRes: state.manageOperators.setBoxOperatorsResponse,
    boxSmsboxesRes: state.manageOperators.listBoxSmsboxesResponse,
    setBoxSmsboxesRes: state.manageOperators.setBoxSmsboxesResponse
  };
}

export default connect(mapStateToProps, {
  listKannelBoxes, upsertKannelBox, listBoxOperators, setBoxOperators,
  listBoxSmsboxes, setBoxSmsboxes, listOperators
})(KannelBoxes);
